/**
 * Utility functions for managing lead form data in the KB interface
 */

/**
 * Returns the initial form data structure for a new lead
 * @returns {Object} Default lead form data
 */
export const getInitialFormData = () => {
  const now = new Date();
  
  return {
    // Basic information
    pointOfContact: '',
    phone: '',
    email: '',
    clientName: '',
    stage: { _id: 'stage_lead_in' },
    leadSource: null,
    assignedTo: null,
    tags: [],
    nextFollowUpTime: null,
    
    // Event details
    eventDates: [],
    events: [{
      type: '',
      guestCount: null,
      startTime: '',
      endTime: '',
      notes: ''
    }],
    
    // Location
    locations: {
      cities: [],
      venues: []
    },
    
    // Budget
    budget: {
      amount: null,
      currency: 'INR',
      notes: ''
    },
    
    // Preferences
    preferences: {
      cuisine: [],
      musicGenre: [],
      specialRequests: ''
    },
    
    // Entertainment
    entertainmentOptions: [],
    
    // Accommodation
    accommodation: {
      required: false,
      roomCount: null,
      residenceGuestCount: null
    },
    
    // Other
    guestCapacity: null,
    notes: '',
    proposal: {
      status: 'pending',
      details: {}
    }
  };
};

/**
 * Validates the lead form data before submission
 * @param {Object} formData - The form data to validate
 * @returns {Object} Validation result with isValid flag and errors array
 */
export const validateFormData = (formData) => {
  // No validation errors - everything is optional
  return {
    isValid: true,
    errors: []
  };
};

/**
 * Transforms form data to match the API requirements
 * @param {Object} formData - The form data to transform
 * @param {Object} metadata - The metadata containing reference data
 * @returns {Object} Transformed data suitable for API submission
 */
export const transformFormDataForSubmission = (formData, metadata) => {
  // Extract stage ID or use default
  const stage = formData.stage;
  const stageId = typeof stage === 'object' ? 
                 (stage._id || stage.id || 'stage_lead_in') : 
                 (stage || 'stage_lead_in');
  
  // Process lead source
  const leadSource = typeof formData.leadSource === 'object' ? 
                   (formData.leadSource?._id || formData.leadSource) : 
                   formData.leadSource;
  
  // Process assigned user
  const assignedTo = typeof formData.assignedTo === 'object' ? 
                    formData.assignedTo?._id : 
                    formData.assignedTo;
  
  // Process events
  console.log('[transformFormDataForSubmission] Processing events:', { 
    events: formData.events, 
    availableTypes: metadata.eventTypes 
  });
  
  // If no events are provided, use an empty array
  const events = (formData.events || [])
    // Filter out empty events but keep at least one empty event if that's all we have
    .filter((event, index, arr) => event.type || (arr.length === 1 && index === 0))
    .map(event => {
      // Handle case where event.type is already the event type object
      if (typeof event.type === 'object' && event.type._id) {
        console.log('[transformFormDataForSubmission] Event type is already an object:', event.type);
        return {
          ...event,
          type: event.type._id
        };
      }
      
      // If event.type is already a string ID and not a name in our metadata, assume it's already an ID
      if (typeof event.type === 'string' && 
          metadata.eventTypes && 
          !metadata.eventTypes.some(t => t.name === event.type)) {
        console.log('[transformFormDataForSubmission] Event type appears to be an ID already:', event.type);
        return event;
      }
      
      // Try to find the event type by name and get its ID
      if (metadata.eventTypes && metadata.eventTypes.length > 0) {
        const eventType = metadata.eventTypes.find(t => 
          (typeof event.type === 'string' && t.name === event.type) || 
          (t._id === event.type)
        );
        
        if (eventType) {
          console.log('[transformFormDataForSubmission] Found matching event type:', { 
            input: event.type, 
            matched: eventType.name,
            id: eventType._id 
          });
          return {
            ...event,
            type: eventType._id
          };
        }
      }
      
      // If we couldn't match, keep the original value
      console.log('[transformFormDataForSubmission] No matching event type found, keeping original:', event.type);
      return event;
    });
  
  return {
    ...formData,
    stage: stageId,
    leadSource: leadSource,
    assignedTo: assignedTo,
    events: events
  };
};

/**
 * Determines if form data has changed compared to original data
 * @param {Object} originalData - The original data from the server
 * @param {Object} currentData - The current form data
 * @returns {boolean} True if data has changed
 */
export const hasFormDataChanged = (originalData, currentData) => {
  if (!originalData) return true;
  
  // Simplify the objects by converting to string for comparison
  // This ignores property order differences and handles nested objects
  const simplifiedOriginal = JSON.stringify(simplifyObjectForComparison(originalData));
  const simplifiedCurrent = JSON.stringify(simplifyObjectForComparison(currentData));
  
  return simplifiedOriginal !== simplifiedCurrent;
};

/**
 * Safely creates a serializable version of an object, handling circular references
 * @param {*} obj - The object to make serializable
 * @param {Set} seen - Set of seen objects (used for circular reference detection)
 * @returns {*} A safe version of the object
 */
const makeSafeForSerialization = (obj, seen = new Set()) => {
  // Handle primitive types or null/undefined
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }
  
  // Handle DOM nodes and instances of classes like HTMLButtonElement
  if (obj instanceof Element || obj instanceof Node || 
      (typeof window !== 'undefined' && obj instanceof window.HTMLElement)) {
    return '[DOM_ELEMENT]';
  }
  
  // Handle Date objects
  if (obj instanceof Date) {
    return obj.toISOString();
  }
  
  // Detect circular references
  if (seen.has(obj)) {
    return '[CIRCULAR]';
  }
  
  // Add current object to seen set
  seen.add(obj);
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => makeSafeForSerialization(item, seen));
  }
  
  // Handle objects
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    // Skip React internals and DOM nodes
    if (key.startsWith('__react') || key.startsWith('_reactFragment')) {
      continue;
    }
    result[key] = makeSafeForSerialization(value, seen);
  }
  
  return result;
};

/**
 * Simplifies an object for comparison by removing properties that
 * shouldn't affect equality determination
 * @param {Object} data - The data object to simplify
 * @returns {Object} Simplified object
 */
const simplifyObjectForComparison = (data) => {
  try {
    // First, create a safe version that can be serialized
    const safeData = makeSafeForSerialization(data);
    
    // Create a deep copy to avoid modifying the original
    let copy;
    try {
      copy = JSON.parse(JSON.stringify(safeData));
    } catch (err) {
      console.error('Error stringifying data:', err);
      // If JSON.stringify fails, just use the safe data directly
      copy = safeData;
    }
    
    // Remove properties that shouldn't be compared
    delete copy.createdAt;
    delete copy.updatedAt;
    delete copy.__v;
    
    // Process events array to standardize type representation
    if (copy.events) {
      copy.events = copy.events.map(event => {
        // If event.type is an object with _id, use only the _id
        if (event.type && typeof event.type === 'object' && event.type._id) {
          return {
            ...event,
            type: event.type._id
          };
        }
        return event;
      });
    }
    
    return copy;
  } catch (error) {
    console.error('Error in simplifyObjectForComparison:', error);
    // Return a simplified version that won't cause errors
    return { safeComparisonFailed: true };
  }
};
